import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Upload, X, Eye } from 'lucide-react';
import { toast } from 'sonner';
import ImagePreview from '../common/ImagePreview';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fileToDataURL = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(file);
});

const DOC_FIELDS = [
  { key: 'photo_url', label: 'Photo' },
  { key: 'cnic_front_url', label: 'CNIC Front' },
  { key: 'cnic_back_url', label: 'CNIC Back' },
  { key: 'signature_url', label: 'Signature' },
  { key: 'thumb_impression_url', label: 'Thumb' },
];

function ImageUploader({ label, value, onChange, onPreview }) {
  const ref = useRef(null);
  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error(`${label} must be under 2MB`);
    onChange(await fileToDataURL(file));
  };
  return (
    <div className="space-y-1.5">
      <Label className="text-slate-300 text-xs">{label}</Label>
      <div className="border border-dashed border-slate-700 rounded-sm p-2 bg-slate-800/40 min-h-[160px] flex items-center justify-center relative">
        {value ? (
          <>
            <img src={value} alt={label} className="max-h-40 max-w-full object-contain rounded-sm" />
            <div className="absolute top-1 right-1 flex gap-1">
              <button type="button" onClick={() => onPreview && onPreview(value, label)}
                data-testid={`preview-${label.toLowerCase().replace(/\s/g, '-')}`}
                className="p-1 bg-amber-500/80 hover:bg-amber-500 rounded-sm text-white" title="Preview">
                <Eye className="h-3 w-3" />
              </button>
              <button type="button" onClick={() => onChange('')}
                className="p-1 bg-red-500/80 hover:bg-red-500 rounded-sm text-white" title="Remove">
                <X className="h-3 w-3" />
              </button>
            </div>
          </>
        ) : (
          <button type="button" onClick={() => ref.current?.click()}
            className="flex flex-col items-center text-slate-500 hover:text-amber-500 text-xs">
            <Upload className="h-4 w-4 mb-1" /> Upload
          </button>
        )}
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={onPick} />
      </div>
    </div>
  );
}

export default function CustomerEditDialog({ customer, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({});
  const [preview, setPreview] = useState({ open: false, src: null, title: '' });

  useEffect(() => { if (customer) setForm({ ...customer, income: customer.income ?? '' }); }, [customer]);

  const save = async () => {
    try {
      const payload = { ...form, income: form.income ? Number(form.income) : null };
      delete payload.id; delete payload.customer_id; delete payload.account_number;
      // Iter36 — tracking_number is now editable (uniqueness enforced backend-side).
      // Only send it when the operator actually changed it; empty string keeps current.
      if (payload.tracking_number === customer.tracking_number) {
        delete payload.tracking_number;
      }
      delete payload.barcode_data; delete payload.qr_data;
      delete payload.created_at; delete payload._id;
      await axios.put(`${API_URL}/api/customers/update/${customer.id}`, payload, { withCredentials: true });
      toast.success('Customer updated');
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  if (!customer) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white">Edit Customer — {customer.name}</DialogTitle>
          <DialogDescription className="text-slate-400">
            Account #{customer.account_number} • Tracking {customer.tracking_number}
          </DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="personal">
          <TabsList className="bg-slate-800 border border-slate-700">
            <TabsTrigger value="personal" data-testid="edit-tab-personal" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">Personal</TabsTrigger>
            <TabsTrigger value="documents" data-testid="edit-tab-documents" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">Documents</TabsTrigger>
            <TabsTrigger value="guarantor" data-testid="edit-tab-guarantor" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">Guarantor</TabsTrigger>
          </TabsList>

          <TabsContent value="personal" className="mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Name</Label>
                <Input value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="edit-customer-name" className="bg-slate-800 border-slate-700 text-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Relation Type</Label>
                <select
                  data-testid="edit-customer-relation-type"
                  value={form.relation_type || (form.husband_name ? 'Husband' : 'Father')}
                  onChange={(e) => setForm({ ...form, relation_type: e.target.value })}
                  className="w-full bg-slate-800 border border-slate-700 text-white rounded-sm h-9 px-3 text-sm">
                  <option value="Father">Father</option>
                  <option value="Husband">Husband</option>
                </select>
              </div>
              <div className="space-y-2 col-span-2">
                <Label className="text-slate-300">{(form.relation_type || (form.husband_name ? 'Husband' : 'Father')) === 'Husband' ? 'Husband Name' : 'Father Name'}</Label>
                <Input
                  data-testid="edit-customer-relation-name"
                  value={(form.relation_type || (form.husband_name ? 'Husband' : 'Father')) === 'Husband' ? (form.husband_name || '') : (form.father_name || '')}
                  onChange={(e) => {
                    const rt = form.relation_type || (form.husband_name ? 'Husband' : 'Father');
                    if (rt === 'Husband') setForm({ ...form, husband_name: e.target.value, father_name: '' });
                    else setForm({ ...form, father_name: e.target.value, husband_name: '' });
                  }}
                  className="bg-slate-800 border-slate-700 text-white" />
              </div>
              {[['cnic', 'CNIC'], ['mobile', 'Mobile'], ['tracking_number', 'Tracking Number'], ['occupation', 'Occupation'], ['department', 'Department'], ['sales_executive_name', 'Sales Executive By'], ['server_name', 'Server Name']].map(([k, l]) => (
                <div key={k} className="space-y-2">
                  <Label className="text-slate-300">{l}</Label>
                  <Input value={form[k] || ''} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    data-testid={`edit-customer-${k.replace(/_/g, '-')}`} className="bg-slate-800 border-slate-700 text-white" />
                </div>
              ))}
              <div className="space-y-2">
                <Label className="text-slate-300">Date of Birth</Label>
                <Input type="date" value={(form.dob || '').slice(0, 10)}
                  onChange={(e) => setForm({ ...form, dob: e.target.value })}
                  data-testid="edit-customer-dob"
                  className="bg-slate-800 border-slate-700 text-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">CNIC Expiry Date</Label>
                <Input type="date" value={(form.cnic_expiry_date || '').slice(0, 10)}
                  onChange={(e) => setForm({ ...form, cnic_expiry_date: e.target.value })}
                  data-testid="edit-customer-cnic-expiry"
                  className="bg-slate-800 border-slate-700 text-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Age (auto)</Label>
                <Input readOnly value={(() => {
                  const d = form.dob ? new Date(form.dob) : null;
                  if (!d || Number.isNaN(d.getTime())) return '';
                  const n = new Date();
                  let a = n.getFullYear() - d.getFullYear();
                  const m = n.getMonth() - d.getMonth();
                  if (m < 0 || (m === 0 && n.getDate() < d.getDate())) a -= 1;
                  return a >= 0 && a < 130 ? `${a}` : '';
                })()} placeholder="—"
                  data-testid="edit-customer-age"
                  className="bg-slate-800/60 border-slate-700 text-amber-500" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Employment Type</Label>
                <select
                  data-testid="edit-customer-employment-type"
                  value={form.employment_type || ''}
                  onChange={(e) => setForm({ ...form, employment_type: e.target.value })}
                  className="w-full bg-slate-800 border border-slate-700 text-white rounded-sm h-9 px-3 text-sm">
                  <option value="">Select…</option>
                  <option value="Government">Government (salaried)</option>
                  <option value="Private">Private (salaried)</option>
                  <option value="Business">Business / Self-employed</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Income (PKR)</Label>
                <Input type="number" value={form.income ?? ''}
                  onChange={(e) => setForm({ ...form, income: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white" />
              </div>
              <div className="space-y-2 col-span-2">
                <Label className="text-slate-300">Address</Label>
                <Textarea value={form.address || ''} onChange={(e) => setForm({ ...form, address: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white" rows={2} />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="documents" className="mt-4">
            <div className="grid grid-cols-3 gap-3">
              {DOC_FIELDS.map((f) => (
                <ImageUploader key={f.key} label={f.label} value={form[f.key]}
                  onChange={(v) => setForm({ ...form, [f.key]: v })}
                  onPreview={(src, label) => setPreview({ open: true, src, title: `${customer?.name || ''} — ${label}` })}
                />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="guarantor" className="mt-4">
            <div className="grid grid-cols-2 gap-4">
              {[['guarantor_name', 'Name'], ['guarantor_cnic', 'CNIC']].map(([k, l]) => (
                <div key={k} className="space-y-2">
                  <Label className="text-slate-300">{l}</Label>
                  <Input value={form[k] || ''} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    data-testid={`edit-${k.replace(/_/g, '-')}`}
                    className="bg-slate-800 border-slate-700 text-white" />
                </div>
              ))}
              <div className="space-y-2">
                <Label className="text-slate-300">Relation Type</Label>
                <select
                  data-testid="edit-guarantor-relation-type"
                  value={form.guarantor_relation_type || (form.guarantor_husband_name ? 'Husband' : 'Father')}
                  onChange={(e) => setForm({ ...form, guarantor_relation_type: e.target.value })}
                  className="w-full bg-slate-800 border border-slate-700 text-white rounded-sm h-9 px-3 text-sm">
                  <option value="Father">Father</option>
                  <option value="Husband">Husband</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">{(form.guarantor_relation_type || (form.guarantor_husband_name ? 'Husband' : 'Father')) === 'Husband' ? 'Husband Name' : 'Father Name'}</Label>
                <Input
                  data-testid="edit-guarantor-relation-name"
                  value={(form.guarantor_relation_type || (form.guarantor_husband_name ? 'Husband' : 'Father')) === 'Husband' ? (form.guarantor_husband_name || '') : (form.guarantor_father_name || '')}
                  onChange={(e) => {
                    const rt = form.guarantor_relation_type || (form.guarantor_husband_name ? 'Husband' : 'Father');
                    if (rt === 'Husband') setForm({ ...form, guarantor_husband_name: e.target.value, guarantor_father_name: '' });
                    else setForm({ ...form, guarantor_father_name: e.target.value, guarantor_husband_name: '' });
                  }}
                  className="bg-slate-800 border-slate-700 text-white" />
              </div>
              {[['guarantor_profession', 'Profession'], ['guarantor_mobile', 'Mobile'], ['guarantor_relation', 'Relation']].map(([k, l]) => (
                <div key={k} className="space-y-2">
                  <Label className="text-slate-300">{l}</Label>
                  <Input value={form[k] || ''} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    data-testid={`edit-${k.replace(/_/g, '-')}`}
                    className="bg-slate-800 border-slate-700 text-white" />
                </div>
              ))}
              <div className="space-y-2 col-span-2">
                <Label className="text-slate-300">Address</Label>
                <Textarea value={form.guarantor_address || ''}
                  onChange={(e) => setForm({ ...form, guarantor_address: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white" rows={2} />
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end space-x-2 border-t border-slate-800 pt-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-300">Cancel</Button>
          <Button onClick={save} data-testid="submit-edit-customer" className="bg-amber-500 hover:bg-amber-600">Save Changes</Button>
        </div>
      </DialogContent>
      <ImagePreview
        open={preview.open}
        onOpenChange={(o) => setPreview((p) => ({ ...p, open: o }))}
        src={preview.src}
        title={preview.title}
      />
    </Dialog>
  );
}
